<template>
    <div class="main-view">
        <div class="usage-header">Usage Overview</div>
        <div class="flex column inputs justify-start gap-5px border-section">
            <div class="flex gap-20">
                <InpComponent 
                    label="Start Date"
                    type="date"
                    key_ref="startDate"
                    :value="startDate"
                    @inputChange="inputChangeFunc"
                    :noMargin="true"
                />
                <InpComponent 
                    label="End Date"
                    type="date"
                    key_ref="endDate"
                    :value="endDate"
                    @inputChange="inputChangeFunc"
                    :noMargin="true"
                />
                <button class="buttons w-content h-content" @click="loadData()">Load Data</button>
            </div>
            <div class="flex gap-20">
                <div class="last-month-item" @click="lastMonthApply()">Last Month</div>
                <div class="last-month-item" @click="thisMonthApply()">This Month</div>
            </div>
        </div>
        <div class="data-area flex column gap-20">
            <div class="blocks">
                <div class="block border-section">
                    <div class="block-value bold font-size-24">{{ totalRows }}</div>
                    <div class="block-title  ">Converted Rows</div>
                </div>
            </div>
            <div class="chart-container border-section">
                <apexchart v-if="dataLoaded" :options="options" :series="options.series" :type="bar" height="300"></apexchart>
                <div v-else class="place-holder">
                    <LoaderItem />
                </div>
            </div>
        </div>
        <div class="border-section distribution-container">
            <div class="section-title">Reports By Type</div>
            <div class="rows-container">
                <table>
                    <tbody v-for="dist of distributionData">
                        <tr>
                            <td class="type-td">
                                <div class="template-type bold">{{ dist.template_type }}</div>
                            </td>
                            <td style="width: 100%;">
                                <div class="template-bar">
                                    <div class="bar-line" :style="`width: ${100 / maxType * dist.count}%`"></div>
                                    <div class="text-bar">{{ dist.count }} ({{ (100 / totalRows * dist.count).toFixed(2) }}%)</div>

                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
        <div class="border-section distribution-container" v-if="user && user.is_super_user && props.full">
            <div class="section-title">Reports By Client</div>
            <div class="rows-container">
                <table>
                    <tbody v-for="dist of splitByClient ">
                        <tr>
                            <td class="type-td text-left">
                                <router-link 
                                    :to="`/admin/users/${dist.account_id}`"
                                    class="template-type bold no-wrap text-left"
                                    target="_blank" 
                                    >{{ dist.username }}
                                </router-link>
                            </td>
                            <td style="width: 100%;">
                                <div class="template-bar">
                                    <div class="bar-line" :style="`width: ${100 / maxClient * dist.count}%`"></div>
                                    <div class="text-bar">{{ dist.count }} ({{ (100 / totalRows * dist.count).toFixed(2) }}%)</div>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
        
            

    </div>
</template>


<script setup>
    import dayjs from 'dayjs';
    import API from '@/api/index.js'
    import { ref, computed, onMounted } from 'vue';
    import InpComponent from '@/components/InpComponent.vue';
    import LoaderItem from '@/components/LoaderItem.vue';
    import store from '@/store/index.js';
    const startDate = ref(dayjs().startOf('month').format('YYYY-MM-DD'))
    const endDate = ref(dayjs().endOf('month').format('YYYY-MM-DD'))
    const dataLoaded = ref(false);
    const totalRows = ref(0);
    const distributionData = ref(null);
    const splitByClient = ref(false);

    const loading = ref(false);

    const user = computed(() => store.state.user)

    const maxClient = computed(() => {
        if (!splitByClient.value || splitByClient.value.length == 0) {
            return 0;
        }
        return Math.max(...splitByClient.value.map(item => item.count));
    })
    const maxType = computed(() => {
        if (!distributionData.value || distributionData.value.length == 0) {
            return 0;
        }
        return Math.max(...distributionData.value.map(item => item.count));
    })

    const props = defineProps({
        account_id: {
            type: String,
            required: false
        },
        full: {
            type: String,
            required: false,
        }
    });

    const options = ref({
        series: [{
            name: "Sales",
            data: []
        }],
        chart: {
            type: 'bar',
            height: 380
        },
        plotOptions: {
            bar: {
                borderRadius: 5, // Makes the bars rounded on top
                borderRadiusApplication: 'end', // Applies the rounding to the top of the bars
                columnWidth: '50%',
                dataLabels: {
                    position: 'top', // Positions the data labels on top of the bars
                }
            }
        },
        dataLabels: {
            enabled: true,
            style: {
                colors: ['#000'] // Sets the color of the data labels to black
            },
            formatter: function (val) {
                return val || ''; // Displays the value as is
            }
        },
        xaxis: {
            type: 'category',
            labels: {
                style: {
                    fontSize: '10px',
                    fontWeight: 700
                },
                formatter: function (val) {
                    return dayjs(val).format('MMM D, YYYY'); // Format the date to a more human-readable format
                }
            }
        },
        title: {
            text: 'Converted Rows',
            align: 'left',
            style: {
                fontSize: '16px',
                fontWeight: 'bold'
            }
        },
        tooltip: {
            x: {
                formatter: function (val) {
                    return `Date: ${dayjs(val).format('MMM D, YYYY')}`; // Format the date in the tooltip
                }
            },
            y: {
                formatter: function (val) {
                    return `Count: ${val}`;
                }
            }
        }
    });

    function lastMonthApply(){
        startDate.value = dayjs().subtract(1, 'month').startOf('month').format('YYYY-MM-DD');
        endDate.value = dayjs().subtract(1, 'month').endOf('month').format('YYYY-MM-DD');
        loadData();
    }
    function thisMonthApply() {
        startDate.value = dayjs().startOf('month').format('YYYY-MM-DD');
        endDate.value = dayjs().endOf('month').format('YYYY-MM-DD');
        loadData();
    }
    function inputChangeFunc(event) {
        const event_type = event[0];
        const event_value = event[1];
        if (event_type == 'startDate') {
            startDate.value = event_value;
        }
        if (event_type == 'endDate') {
            endDate.value = event_value;
        }
    }
    async function loadData(){
        if (loading.value){
            return;
        }
        try {
            totalRows.value = 0;
            loading.value = true; 
            dataLoaded.value = false;
            const response = await API.FIUDashboardData(startDate.value, endDate.value, props.account_id, props.full);
            const distribtionResponse = await API.getDistributionData(startDate.value, endDate.value, props.account_id, props.full);
            const respData = response.filledRows;
            distributionData.value = distribtionResponse.clientsDistribution;
            if (distributionData.value.length > 0) {
                distributionData.value = distributionData.value.sort((a, b) => b.count - a.count);
            }

            if (props.full) {
                const clientData  = await API.getDistributionDataByClient(startDate.value, endDate.value);
                splitByClient.value = clientData.clientsDistribution;
                if (splitByClient.value.length > 0) {
                    splitByClient.value = splitByClient.value.sort((a, b) => b.count - a.count);
                }
            }

            options.value.series[0].data = respData.map(item => {
                totalRows.value += item.count;
                return {
                    x: item.date,
                    y: item.count
                }
            })
            dataLoaded.value = true;
        } catch (error) {
            console.error('Error loading data:', error);
        } finally {
            loading.value = false; 
        }
        
    }
    onMounted(async () => {
        document.title = 'Usage Dashboard';
        await loadData();
    });

</script>


<style lang="scss" scoped>

    table {
        width: 100%;
        border-collapse: collapse;
    }
    .main-view {
        padding: 20px;
        display: flex;
        flex-direction: column;
        gap: 20px
    }
    .chart-container {
        background-color: white;
        border-radius: 10px;
        padding: 20px;
    }
    .distribution-container {
        background-color: white;
        border-radius: 10px;
        padding: 20px;
        width: 700px;
    }
    .section-title {
        font-size: 18px;
        font-weight: 600;
        color: black;
        margin-bottom: 30px;
        text-align: left;
    }
    .text-bar {
        white-space: nowrap;
    }
    .inputs {
        background-color: white;
        padding: 20px;
        display: flex;
        align-items: flex-start;
        border-radius: 10px;
        width: fit-content;
    }
    .last-month-item {
        cursor: pointer;
        color: rgb(74, 74, 74);
        &:hover {
            color: rgb(97, 97, 97);
        }
    }
    .bar-line {
        height: 15px;
        background-color: rgb(0, 143, 251);
        border-radius: 5px;
    }
    .type-td {
        border-right: 1px solid rgb(71, 71, 71);
        padding-right: 10px;
    }
    .template-type {
        text-align: left;
        padding: 5px 0px;
    }
    .template-bar {
        padding-left: 10px;
        width: 100%;
        text-align: left;
        display: flex;
        align-items: center;
        gap: 10px;

    }
    .place-holder {
        display: flex;
        justify-content: center;
        align-items: center;
        height: 300px;
    }
    .blocks {
        display: flex;
        gap: 20px;
    }
    .block {
        background-color: white;
        padding: 20px;
        border-radius: 10px;
        display: flex;
        flex-direction: column;
        align-items: center;
        width: fit-content;
    }
    .block-title {
        color: rgb(112, 115, 119);
    }
    .usage-header {
        font-size: 24px;
        font-weight: 600;
        color: black;
        text-align: left;
    }
</style>